import { FastField, Field, FieldArray, FormikProps, getIn, setIn } from "formik";
import * as Yup from "yup";
import { Button, Box, FormHelperText, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material";
import { FieldTypes } from "./FieldTypes";
import { FormField } from "./FormField";
import { VisibilityCondition } from "./VisibilityCondition";
import { FormFieldTrigger } from "./FormFieldTrigger";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import MultipleAutocompleteField from "./Fields/MultipleAutocomplete";
import PaginatedMultipleAutocompleteField from "./Fields/PaginatedMultipleAutocomplete";

import { MultipleChoiceField, RadioButtonsField, CheckboxField } from "./Fields/ChoiceFields";
import { ReadOnlyDropDownListField, ReadOnlyTextField, ReadOnlyChip, ReadOnlyMultipleChips } from "./Fields/ReadOnlyFields";
import { DatePickerField, DateTimePickerField } from "./Fields/DateFields";
import { MtcTextField, MultipleLineTextField, DropDownListField, AutocompleteField } from "./Fields/TextFields";
import { RichTextEditorField, ActionField, HeaderField, ReturnDisplayField } from "./Fields/OtherFields";

dayjs.extend(utc);

export { FastField, Field };

export const useFormStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: "Flex",
    justifyContent: "center",
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + parseInt(theme.spacing(3)) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-center",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  formControl: {
    minWidth: 120,
  },
  error: {
    color: theme.palette.error.main,
  },
}));

function getDefaultValueForField(field: FormField) {
  switch (field.type) {
    case FieldTypes.DatePicker:
    case FieldTypes.DateTimePicker:
      return null;
    case FieldTypes.FieldArray:
      return [];
    default:
      return "";
  }
}

export function getFormValues(
  fieldValues: Record<string, unknown>,
  fields: FormField[],
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  fields.forEach((field: FormField) => {
    values[field.id] =
      fieldValues[field.id] || field.value || getDefaultValueForField(field);
  });
  return values;
}

/**
 * The inverse of `getFormValues`: turn "untouched" back into "absent" before the values go to the
 * API.
 *
 * A select needs `""` as its empty value or MUI treats it as uncontrolled, so every unset dropdown
 * carries an empty string. Posted as-is, a field the server models as a nullable number (`int?`)
 * fails to deserialize — and because the failure is in the body, model binding produces the
 * confusing pair "the <param> field is required" plus a converter error naming the real culprit.
 *
 * Only empty strings on choice-backed fields are touched, so a genuinely blank text field still
 * posts `""` rather than silently becoming null.
 */
export function normalizeFormValues(
  values: Record<string, unknown>,
  fields: FormField[],
): Record<string, unknown> {
  const choiceFields = new Set(
    fields
      .filter(
        (field) =>
          field.type === FieldTypes.DropDownList ||
          field.type === FieldTypes.RadioButtons ||
          field.type === FieldTypes.Autocomplete,
      )
      .map((field) => field.id),
  );

  const out: Record<string, unknown> = { ...values };
  choiceFields.forEach((id) => {
    if (out[id] === "") out[id] = null;
  });
  return out;
}

interface ValidationTest {
  name: string;
  message: string;
  testFunction: (parent: Record<string, unknown>) => boolean;
}

export function getFormValidationSchema(
  fields: FormField[],
  tests?: ValidationTest[],
) {
  const shape: Record<string, Yup.AnySchema> = {};
  fields.forEach((field: FormField) => {
    if (field.yupSchema) {
      shape[field.id] = field.yupSchema;
    }
  });
  if (tests) {
    tests.forEach((test) => {
      shape[test.name] = Yup.mixed().test(test.name, test.message, function () {
        return test.testFunction(this.parent);
      });
    });
  }
  return Yup.object().shape(shape);
}

export function MtcFieldArray(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
  triggers?: FormFieldTrigger[];
}) {
  const { formik, field, classes, variant, triggers } = props;

  return (
    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} key={field.id}>
      <FieldArray name={field.id}>
        {({ remove, push }) => (
          <>
            {getIn(formik.values, field.id).length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  width: "100%",
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                  border: "1px dashed",
                  borderColor: "grey.300",
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  No {field.text?.toString().toLowerCase() || "items"} added yet
                </Typography>
              </Box>
            ) : (
              getIn(formik.values, field.id).map((_: unknown, index: number) => {
                const currentItemValues = getIn(formik.values, `${field.id}[${index}]`);

                return (
                  <Box sx={{ display: "flex", alignItems: "center", pb: 1 }} key={index}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={1} key={index}>
                        {field.subfields?.map((subfield: FormField) => {
                          let isVisible = true;
                          if (subfield.visibilityFunction) {
                            isVisible = subfield.visibilityFunction(currentItemValues);
                          }
                          if (subfield.visibilityConditions) {
                            isVisible = subfield.visibilityConditions.every(
                              (condition: VisibilityCondition) =>
                                currentItemValues[condition.fieldId] === condition.fieldAnswerValueString,
                            );
                          }
                          if (!isVisible) return null;

                          const fieldWithUpdatedId = {
                            ...subfield,
                            id: `${field.id}[${index}].${subfield.id}`,
                            visibilityFunction: undefined,
                            visibilityConditions: undefined,
                          };

                          return (
                            <MtcFormField
                              key={`${subfield.id}-${index}`}
                              formik={formik}
                              field={fieldWithUpdatedId}
                              classes={classes}
                              variant={variant}
                              triggers={triggers}
                            />
                          );
                        })}
                      </Grid>
                    </Box>
                    <Box sx={{ ml: 1 }}>
                      <IconButton aria-label="clear field" onClick={() => remove(index)}>
                        <HighlightOffIcon />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
            {/* An error on the array ITSELF (`.min(1, "Add at least one…")`) belongs to no single
                input, so nothing rendered it. Now that Form.tsx actually collects it, show it — an
                error that blocks submission while staying invisible is worse than none at all. */}
            {typeof getIn(formik.errors, field.id) === "string" && (
              <Grid size={{ xs: 12 }}>
                <FormHelperText error>
                  {getIn(formik.errors, field.id) as string}
                </FormHelperText>
              </Grid>
            )}
            <Grid>
              <Button
                type="button"
                onClick={() =>
                  push(
                    Object.fromEntries(
                      (field.subfields || []).map((subfield) => [
                        subfield.id,
                        getDefaultValueForField(subfield),
                      ]),
                    ),
                  )
                }
              >
                {field.addButtonText || "Add Another"}
              </Button>
            </Grid>
          </>
        )}
      </FieldArray>
    </Grid>
  );
}

export function MtcFormField(props: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
  classes: Record<string, string>;
  variant: "filled" | "outlined" | "standard";
  triggers?: FormFieldTrigger[];
}) {
  const { formik, field, classes, variant, triggers } = props;
  let visible = true;

  if (field.visibilityFunction) {
    if (!field.visibilityFunction(formik.values)) {
      setIn(formik.values, field.id, "");
      visible = false;
    }
  }

  if (field.visibilityConditions) {
    field.visibilityConditions.forEach((condition: VisibilityCondition) => {
      if (formik.values[condition.fieldId] !== condition.fieldAnswerValueString) {
        visible = false;
      }
    });
  }

  if (!visible) {
    setIn(formik.values, field.id, "");
    return <></>;
  }

  switch (field.type) {
    case FieldTypes.Action:
      return <ActionField field={field} />;
    case FieldTypes.MultipleChoice:
      return <MultipleChoiceField formik={formik} field={field} />;
    case FieldTypes.RadioButtons:
      return <RadioButtonsField formik={formik} field={field} />;
    case FieldTypes.FieldArray:
      return <MtcFieldArray formik={formik} field={field} classes={classes} variant={variant} triggers={triggers} />;
    case FieldTypes.DropDownList:
      return <DropDownListField formik={formik} field={field} classes={classes} variant={variant} triggers={triggers} />;
    case FieldTypes.Text:
      return <MtcTextField formik={formik} field={field} classes={classes} variant={variant} triggers={triggers} />;
    case FieldTypes.Display:
      return <ReturnDisplayField field={field} />;
    case FieldTypes.Header:
      return <HeaderField field={field} />;
    case FieldTypes.MultiLineText:
      return <MultipleLineTextField formik={formik} field={field} classes={classes} variant={variant} />;
    case FieldTypes.DatePicker:
      return <DatePickerField field={field} formik={formik} variant={variant} classes={classes} />;
    case FieldTypes.DateTimePicker:
      return <DateTimePickerField field={field} formik={formik} variant={variant} classes={classes} />;
    case FieldTypes.Checkbox:
      return <CheckboxField formik={formik} field={field} />;
    case FieldTypes.Autocomplete:
      return <AutocompleteField formik={formik} field={field} classes={classes} variant={variant} />;
    case FieldTypes.MultipleAutocomplete:
      return <MultipleAutocompleteField formik={formik} field={field} classes={classes} variant={variant} />;
    case FieldTypes.MultipleAutocompleteWithSearch:
      return <PaginatedMultipleAutocompleteField formik={formik} field={field} classes={classes} variant={variant} />;
    case FieldTypes.RichTextEditor:
      return <RichTextEditorField formik={formik} field={field} />;
    default:
      return "";
  }
}

export function ReadOnlyFormField(props: {
  field: FormField;
  allFieldValues: Record<string, unknown>;
}) {
  const { field, allFieldValues } = props;

  if (field.visibilityFunction && allFieldValues) {
    if (!field.visibilityFunction(allFieldValues)) return "";
  }

  if (field.visibilityConditions) {
    field.visibilityConditions.forEach((condition: VisibilityCondition) => {
      if (allFieldValues[condition.fieldId] !== condition.fieldAnswerValueString) {
        return <></>;
      }
    });
  }

  switch (field.type) {
    case FieldTypes.DropDownList:
      return ReadOnlyDropDownListField(field);
    case FieldTypes.Text:
      return ReadOnlyTextField(field);
    case FieldTypes.Autocomplete:
      return ReadOnlyChip(field);
    case FieldTypes.MultipleAutocomplete:
      return ReadOnlyMultipleChips(field);
    default:
      return "";
  }
}

// TODO TH: Clean this up as much as possible and then migrate to React Hooks Form for performance gains
